import { useEffect } from 'react';

const BASE_URL = 'https://propertytaxhero.com';

interface SeoConfig {
  title: string;
  description: string;
  path: string;
  ogType?: string;
  noindex?: boolean;
  schema?: Record<string, unknown> | Record<string, unknown>[];
}

function setOrCreateMeta(name: string, content: string, isProperty = false) {
  const attr = isProperty ? 'property' : 'name';
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setOrCreateLink(rel: string, href: string) {
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export function usePageSeo({ title, description, path, ogType = 'website', noindex = false, schema }: SeoConfig) {
  useEffect(() => {
    const canonicalUrl = `${BASE_URL}${path}`;

    document.title = title;

    // Standard meta
    setOrCreateMeta('description', description);

    // Canonical
    setOrCreateLink('canonical', canonicalUrl);

    // Robots
    if (noindex) {
      setOrCreateMeta('robots', 'noindex, nofollow');
    } else {
      // Remove noindex if previously set
      const robotsMeta = document.querySelector('meta[name="robots"]');
      if (robotsMeta) robotsMeta.remove();
    }

    // Open Graph
    setOrCreateMeta('og:title', title, true);
    setOrCreateMeta('og:description', description, true);
    setOrCreateMeta('og:url', canonicalUrl, true);
    setOrCreateMeta('og:type', ogType, true);
    setOrCreateMeta('og:site_name', 'PropertyTaxHero', true);
    setOrCreateMeta('og:image', `${BASE_URL}/og-image.png`, true);
    setOrCreateMeta('og:image:width', '1200', true);
    setOrCreateMeta('og:image:height', '630', true);

    // Twitter
    setOrCreateMeta('twitter:title', title);
    setOrCreateMeta('twitter:description', description);
    setOrCreateMeta('twitter:card', 'summary_large_image');
    setOrCreateMeta('twitter:image', `${BASE_URL}/og-image.png`);

    // Schema
    const schemaId = 'page-schema-dynamic';
    const existing = document.getElementById(schemaId);
    if (existing) existing.remove();

    if (schema) {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = schemaId;
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    }

    return () => {
      const s = document.getElementById(schemaId);
      if (s) s.remove();
    };
  }, [title, description, path, ogType, noindex, schema]);
}
